/**
 * Single source of truth for every site animation — durations, delays,
 * easings. Adjust here; all consumers read from this.
 *
 * Layout:
 *   1. Easings   — named curves used everywhere (CSS strings, JS tuples, JS fns)
 *   2. Shared    — animations that span multiple pages (titleReveal, pageTransition, nav, footer)
 *   3. Index     — landing-page motion (Framer Motion, menu, scroll snap)
 *   4. About     — book-page motion (icon keyframes, page rail, background scrolls)
 *   5. Portfolio — carousel page (scene, scroll rail, scroll-to-slide, recede)
 *   6. CaseStudy — slide-over case-study pages (panel, chrome sync, image fade)
 */
import { cubicBezier } from "../utils/easing";

// ═════════════════════════════════════════════════════════════════════
// 1. EASINGS
// ═════════════════════════════════════════════════════════════════════

/** Named easings — every animation on the site speaks one of these seven.
 *  Pick by job, not by feel:
 *  - `ui`       → most opacity/scale/translate on small elements.
 *  - `chrome`   → big-surface motion moving INTO place and staying (panel slides, nav cross-fades, footer fades).
 *  - `sweep`    → fast sweep across the screen with no landing pause (page-transition curtains). CSS twin of GSAP's `power2.out`.
 *  - `enter`    → 3D scene objects arriving with aggressive deceleration, "physical weight" character (office, floor mat, background shapes, book canvas). Tailwind's default `ease-out`.
 *  - `gentle`   → 2D editorial content fading into place with an even decelerated approach (section fades, subtitles, link reveals). Matches Framer Motion's default `easeOut`.
 *  - `draw`     → stroke-drawing animations (gentle ease-in-out, asymmetric).
 *  - `navlink`  → symmetric cubic ease-in-out for hover state pairs. */
export const easings = {
    ui: "cubic-bezier(0.4, 0, 0.2, 1)",
    chrome: "cubic-bezier(0.85, 0, 0.2, 1)",
    sweep: "cubic-bezier(0.33, 1, 0.68, 1)",
    enter: "cubic-bezier(0, 0, 0.2, 1)",
    gentle: "cubic-bezier(0, 0, 0.58, 1)",
    draw: "cubic-bezier(0.4, 0, 0.6, 1)",
    navlink: "cubic-bezier(0.645, 0.045, 0.355, 1)",
} as const;

/** Four control points for each named easing, for JS APIs that take a
 *  bezier-tuple format (Framer Motion accepts arrays directly). Use the
 *  pre-built `easingFns` below for APIs that need a function. */
export const easingTuples = {
    ui: [0.4, 0, 0.2, 1] as const,
    chrome: [0.85, 0, 0.2, 1] as const,
    sweep: [0.33, 1, 0.68, 1] as const,
    enter: [0, 0, 0.2, 1] as const,
    gentle: [0, 0, 0.58, 1] as const,
    draw: [0.4, 0, 0.6, 1] as const,
    navlink: [0.645, 0.045, 0.355, 1] as const,
};

/** Pre-built `(t) => number` evaluators for the named easings. Use these
 *  for GSAP (`ease: easingFns.sweep`) and Lenis `scrollTo` (`easing:
 *  easingFns.chrome`). Both APIs accept functions natively, while GSAP
 *  silently falls back to `power1.out` if handed a CSS cubic-bezier
 *  *string*, so the wrong value here is invisible until you A/B the
 *  curves. Build once at module load. */
export const easingFns = {
    ui: cubicBezier(...easingTuples.ui),
    chrome: cubicBezier(...easingTuples.chrome),
    sweep: cubicBezier(...easingTuples.sweep),
    enter: cubicBezier(...easingTuples.enter),
    gentle: cubicBezier(...easingTuples.gentle),
    draw: cubicBezier(...easingTuples.draw),
    navlink: cubicBezier(...easingTuples.navlink),
};

export const animationConfig = {
    // ═════════════════════════════════════════════════════════════════
    // 2. SHARED — animations that span multiple pages
    // ═════════════════════════════════════════════════════════════════

    /** Per-character title reveal — SHARED by the portfolio AnimatedTitle,
     *  the case-study AnimatedHeading, and the index AvatarUI hero. (The
     *  index.html static hero is a different, pre-JS animation — see above.) */
    titleReveal: {
        /** Hold (hidden) before titles replay their entrance when
         *  returning from a case study to the portfolio. */
        replayDelayMs: 200,
        /** Desktop: delay after a slide centers before the title plays. */
        visibleDelayMs: 150,
        /** Per-character stagger for the title rotate-in. */
        charStaggerMs: 25,
        easing: easings.ui,
        /** h1 + per-character outer translate duration (seconds). */
        titleDurationS: 0.45,
        /** Per-character opacity / rotateY durations (seconds). */
        charOpacityDurationS: 0.45,
        charRotateDurationS: 0.35,
        /** Subtitle / button / status fade-up duration (seconds). */
        fadeUpDurationS: 0.4,
        /** Opacity-only fade-ins (About subtitle and headshot, fadeIn in
         *  CaseStudy/fadeUp.ts): linear, a plain dissolve with no
         *  deceleration, since nothing moves. */
        fadeInEasing: "linear",
        /** Cascading fade-up start delays (ms). The About panel's two
         *  start while the title's second line is still revealing (its
         *  last char begins ≈ 450 ms after the enter delay) so
         *  title → subtitle → headshot read as one cascade. */
        fadeUpDelaysMs: {
            subtitle: 120,
            button: 150,
            status: 180,
            statusCurrent: 200,
            aboutSubtitle: 400,
            aboutImage: 600,
        },
    },

    /** Route-change page-transition overlay (PageTransition.tsx): the
     *  curtain blocks that cover/reveal the screen between routes, and
     *  the logo draw + fill that plays inside them while the next page
     *  loads. Curtains use the `sweep` curve — fast sweep, no landing
     *  pause (CSS twin of GSAP's `power2.out`, what the curtains were
     *  originally tuned against). The logo's supporting micro-motions
     *  use `ui`; the stroke draw uses `draw`. */
    pageTransition: {
        /** Curtain blocks scaling in to cover the outgoing page. */
        curtainCover: {
            durationS: 0.6,
            staggerS: 0.05,
            easing: easings.sweep,
        },
        /** Curtain blocks scaling out to reveal the new page. */
        curtainReveal: {
            durationS: 0.6,
            staggerS: 0.04,
            easing: easings.sweep,
        },
        /** Logo overlay + loader fade-out, right before the curtains open. */
        loaderFadeOut: {
            durationS: 0.25,
            easing: easings.ui,
        },
        /** Hairline guide outline fading IN before the stroke draws. */
        logoGuideIn: {
            durationS: 0.5,
            easing: easings.ui,
        },
        /** Logo container scaling up while the stroke draws. */
        logoContainerScale: {
            durationS: 1.2,
            easing: easings.ui,
            startScale: 0.85,
        },
        /** Fill fading in once the outline has closed (the page is ready
         *  by then, so it's kept short: it's the last wait before the
         *  curtains open). */
        logoFillIn: {
            durationS: 0.5,
            easing: easings.ui,
        },
        /** Hairline guide outline fading OUT as the fill takes over. */
        logoGuideOut: {
            durationS: 0.5,
            easing: easings.ui,
        },
        /** Resting opacity of the hairline guide once it has faded in,
         *  strong enough to show the shape the spinner is tracing. */
        guideOpacity: 0.12,
        /** The stroke is a spinner from the start: its head draws out an
         *  arc and the tail follows the head's exact motion a moment later,
         *  so both ends move at one speed and the arc keeps its length.
         *  When the page is ready the tail eases to a stop and the head,
         *  still at the same speed, closes the outline; then the fill fades
         *  in. The counter is paced to land on 100 as the fill completes,
         *  and the curtains open on the fully formed logo. */
        logoSpin: {
            /** Revolutions per second, along the outline's draw direction. */
            speedRevPerS: 0.75,
            /** Arc length while spinning, as a fraction of the outline. */
            arcLength: 0.3,
            /** Each end's ramp up to speed (and the tail's ramp down to a
             *  stop), so nothing pops in or halts dead (s). */
            rampS: 0.25,
        },
        /** Maximum time to wait for a page to signal ready before forcing
         *  reveal. */
        readyTimeoutMs: 8000,
        /** The numbered counter (NumberedLoader.tsx). Its digits are
         *  odometer wheels the compositor plays from a trajectory baked
         *  ahead of time (counterMotion.ts), so they keep rolling while
         *  the next page's mount stalls the main thread. */
        counter: {
            /** Simulated progress on a route change: an exponential
             *  approach to 100 with this time constant (ms), 63% at one
             *  and 86% at two, that never arrives. The sprint does. */
            simulatedTauMs: 2000,
            /** The digits follow that simulated value through two
             *  cascaded lags of this time constant (ms) each. (The
             *  entrance's real progress has its own: see
             *  portfolio.entrance.progress.smoothingMs.) */
            lagMs: 150,
            /** The fastest the digits roll while following (units per
             *  second), in either mode: a flood of bytes becomes a steady
             *  climb, not a blur. Six turns of the units wheel a second. */
            maxUnitsPerS: 60,
            /** How long the digits take to get from rest up to that
             *  speed (ms): a flood that arrives while they crawl is a
             *  swell, not a kick. */
            rampMs: 150,
            /** Once the page is ready: the run from wherever the digits
             *  are, at the speed they have, to a standstill on 100. Paced
             *  by the distance left at `unitsPerS`, held between `minMs`
             *  and `maxMs`. The same pace as the cap, so the finish is
             *  the climb carrying on and easing out, not a dash. On a
             *  route change the run's length is instead the time the
             *  transition's logo takes to come to rest (always longer),
             *  so the two finish together; `minMs` still holds. */
            sprint: {
                unitsPerS: 60,
                minMs: 450,
                maxMs: 1200,
            },
            /** Trajectory sample spacing (ms): one compositor keyframe
             *  per sample per wheel. */
            sampleMs: 8,
        },
    },

    /** Top-level Nav link hovers (Nav.tsx). Symmetric curve because
     *  hover/unhover are equally weighted. */
    nav: {
        linkHover: {
            /** Tailwind default `transition-opacity` duration is 150ms; we
             *  match that here so the value is discoverable, even though
             *  the components apply it inline (no Tailwind class for it). */
            durationMs: 150,
            easing: easings.navlink,
        },
    },

    /** Top-level Footer animations (Footer.tsx). */
    footer: {
        /** Footer email reveal — block of lines that slides up on hover to
         *  reveal the email address, plus the "Copy to clipboard" → "Copied"
         *  toggle that fires on click. Top and bottom lines are independent
         *  so each can be dialled separately (e.g. a slower top line + a
         *  snappier bottom line for an asymmetric feel). */
        emailReveal: {
            /** Top-line slide-up transition on hover. */
            topLine: {
                durationMs: 350,
                easing: easings.navlink,
            },
            /** Bottom-line slide-up transition on hover. The bottom line
             *  lags the top by `delayMs` on hover-IN so the two lines
             *  don't read as one block; on hover-OUT both lines snap back
             *  together with no delay (handled conditionally in Footer.tsx). */
            bottomLine: {
                durationMs: 350,
                easing: easings.navlink,
                delayMs: 100,
            },
            /** Inner "Copy to clipboard" → "Copied" toggle, click-driven.
             *  Defaults to the same shape as the hover reveal; override
             *  here to give the confirmation a different feel (e.g. a
             *  longer duration so the affirmative state holds visually). */
            copyConfirm: {
                durationMs: 350,
                easing: easings.navlink,
            },
        },
    },

    // ═════════════════════════════════════════════════════════════════
    // 3. INDEX (landing page)
    // ═════════════════════════════════════════════════════════════════

    /** Index page Framer Motion section/camera/skills timings. Most use 1s
     *  with a delay chain; the timings here keep the chain legible at a
     *  glance. Everything that "arrives" — section fades, subtitles,
     *  progress bars, tab content, contact links, office scene, character
     *  movement, 3D shape entries — shares the `gentle` curve (even
     *  decelerated approach). The only exception is `avatarExperience.
     *  backgroundGroup`, which moves between sections rather than entering
     *  and uses `navlink` for its symmetric character. */
    landing: {
        /** Default section fade-up (used by every gated section). */
        sectionFade: {
            durationS: 1,
            delayS: 0,
            bezier: easingTuples.gentle,
        },
        /** Subtitle / secondary line under the main section heading. */
        sectionSubtitle: {
            durationS: 1,
            delayS: 0.6,
            bezier: easingTuples.gentle,
        },
        /** Per-bar entrance for ProgressBars in the skills section.
         *  `delayBaseS + index * delayStepS`. */
        progressBar: {
            durationS: 1,
            delayBaseS: 0.3,
            delayStepS: 0.15,
            bezier: easingTuples.gentle,
        },
        /** Skills tab content fade-in (text under the active tab). */
        tabContent: {
            durationS: 1,
            delayS: 0.2,
            bezier: easingTuples.gentle,
        },
        /** Contact section links, staggered by hand (not by index). */
        contactLinks: {
            durationS: 1,
            delaysS: { first: 0.3, second: 1.0, third: 1.3 },
            bezier: easingTuples.gentle,
        },
        /** Office scene fade / scale / chair-slide. */
        office: {
            sceneFade: { durationS: 1, delayS: 0, bezier: easingTuples.gentle },
            groupScaleIn: { durationS: 1, delayS: 0.5, bezier: easingTuples.gentle },
            /** Chair sliding into the desk after the office settles in. */
            chairSlide: { durationS: 0.45, delayS: 2.1, bezier: easingTuples.gentle },
        },
        /** Character section transition (avatar moving between sections). */
        characterSection: {
            durationS: 1,
            bezier: easingTuples.gentle,
        },
        /** Avatar experience camera tweens. Currently the camera is driven
         *  by `menuSpring` (so it moves with the menu) — this entry is the
         *  fallback tuple if you ever swap from spring to tween. */
        cameraTween: {
            durationS: 1,
            bezier: easingTuples.enter,
        },
        /** AvatarExperience scene-level animations (office Y offset, floor
         *  mat scale-in + opacity, decorative background shapes). All use
         *  the `enter` curve (aggressive deceleration — 3D objects landing
         *  with physical weight) except the background-shape group's
         *  position tween, which uses navlink (symmetric) because it moves
         *  between sections, not on entry. */
        avatarExperience: {
            officeY: { durationS: 1, bezier: easingTuples.enter },
            floorMat: { durationS: 1, delayS: 0, bezier: easingTuples.enter },
            floorMatOpacity: { durationS: 1, delayS: 0.5, bezier: easingTuples.enter },
            backgroundGroup: { durationS: 1, bezier: easingTuples.navlink },
            backgroundShapes: {
                white:  { durationS: 0.8, delayS: 1.0, bezier: easingTuples.enter },
                black:  { durationS: 0.8, delayS: 1.8, bezier: easingTuples.enter },
                box:    { durationS: 0.8, delayS: 1.5, bezier: easingTuples.enter },
            },
        },
        /** Skills tab cards: lift on hover / active state.
         *  Exposed as CSS custom properties (`--tabs-transition-duration` /
         *  `--tabs-transition-easing`) so the static stylesheet rules in
         *  `src/styles/index.css` can read the same numbers without a JS
         *  bridge per element. Set once on `:root` from JS. */
        skillsTabs: {
            durationS: 0.2,
            easing: easings.navlink,
        },
        /** Side-panel "Table of Contents" navigation (Menu.tsx) — desktop
         *  only (≥1300px). Hover-state tweens use the navlink curve to
         *  match the global Nav. The aside slide itself is a spring, see
         *  `menuSpring` below. */
        menu: {
            /** Toggle button (top-right "01 / 03") opacity hover. Tailwind's
             *  default `transition-opacity` duration is 150 ms; mirrored
             *  here for discoverability. */
            toggle: { durationMs: 150, easing: easings.navlink },
            /** Hover-reveal "· Contents" label that slides in from 0
             *  max-width when the toggle is hovered. */
            labelReveal: { durationMs: 500, easing: easings.navlink },
            /** Per-item opacity hover (Intro / Skills / Contact). */
            button: { durationMs: 150, easing: easings.navlink },
        },
        /** Shared Framer Motion spring driving the Menu aside slide-in/out
         *  AND every AvatarExperience camera tween (zoom, position, lookAt).
         *  Coupling the two through one spring means the menu and the 3D
         *  scene move as one coordinated motion when the menu opens. Was
         *  previously in `src/components/Index/Config.tsx`; centralized here
         *  with the rest of the animation tokens. `as const` preserves the
         *  literal `"spring"` so Framer Motion's strict transition type
         *  accepts the object. */
        menuSpring: {
            type: "spring",
            mass: 5,
            stiffness: 500,
            damping: 55,
            restDelta: 0.001,
        } as const,
    },

    /** Index page section-snap scroll (ScrollManager.tsx). When the user
     *  scrolls or clicks a section nav button, the ScrollControls container
     *  GSAP-tweens its `scrollTop`. Uses the `sweep` curve — same exact
     *  shape as GSAP's `power2.out` (what the snap was originally tuned
     *  against) but routed through the named-curve catalog so any future
     *  tuning of `sweep` propagates here. `easing` is the CSS-string twin,
     *  kept for reference; the component imports `easingFns.sweep` to get
     *  the function form GSAP needs. */
    scrollManager: {
        sectionSnap: {
            durationS: 0.9,
            easing: easings.sweep,
            /** Wheel lock window after a snap (ms) so a fast trackpad doesn't
             *  fire a second snap mid-animation. Keep ≥ durationS * 1000. */
            wheelLockMs: 900,
        },
    },

    // ═════════════════════════════════════════════════════════════════
    // 4. ABOUT (book page)
    // ═════════════════════════════════════════════════════════════════

    /** About page (BookUI) ambient micro-loops + page rail. Icon keyframes
     *  live as CSS in `src/styles/index.css`, with their durations surfaced
     *  here and pushed onto `:root` as CSS custom properties (the keyframe
     *  `animation` shorthands read those vars). */
    about: {
        /** Book canvas fade-up: the 3D canvas starts invisible and 48px
         *  below its rest position, then fades + slides up once the
         *  PageTransition has finished revealing the page (`entered=true`).
         *  Uses the `enter` curve, shared with the Index 3D scene entries —
         *  same "3D object arriving with weight" character. */
        bookCanvasEnter: {
            durationMs: 700,
            easing: easings.enter,
            delayMs: 200,
            enterFromPx: 48,
        },
        /** The About panel's door to this page: a little book that flies
         *  (Portfolio/FlyingBook). It runs in its own render loop, so the
         *  timings are in Hz and seconds, not CSS. */
        flyingBook: {
            /** Wing beats per second, at rest and while hovered. */
            flapHz: 1.4,
            hoverFlapHz: 2.2,
            /** One lap of the drift path (a lazy figure-eight). */
            driftPeriodS: 9,
            /** Hover response: the lambda of an exponential approach
             *  (three's MathUtils.damp), so it eases in and out alike. */
            hoverLambda: 6,
            /** The canvas fades in once the first frame is drawn. */
            enter: { durationMs: 600, easing: easings.gentle },
            /** Building the book (its WebGL renderer, shaders compiled, art
             *  uploaded) stalls a phone for a few frames, which shows under
             *  any animation and not at all under none. So Portfolio builds
             *  it ahead of time, once the carousel has been left alone for
             *  this long after its entrance (warmFlyingBook); the About
             *  panel then only attaches it. */
            warmIdleMs: 1500,
            /** Failing that (About opened first), when the book is built
             *  (AboutPanel). It waits until
             *  nothing is animating: this long after the panel's entrance
             *  (title, subtitle, headshot) has ended... */
            mountAfterEntranceMs: 200,
            /** ...and until its strip is this close to the viewport
             *  (IntersectionObserver rootMargin): on a phone the strip is
             *  far below, so the book is built as the reader scrolls
             *  toward it; on a desktop it is in view at once. */
            mountRootMargin: "100%",
        },
        soundWave: { durationS: 1.3 },     // pulse, infinite
        muteShake: { durationS: 0.6 },     // shake, one-shot
        shackleFloat: { durationS: 1.6 },  // float, infinite
        lockPress: { durationS: 0.35 },    // press, one-shot
        /** Two-row seamless infinite scroll behind the book content. Lives
         *  in `tailwind.config.js` because Tailwind's keyframe plugin builds
         *  the utility classes at compile time — runtime cannot rewrite it.
         *  Mirrored here so the duration is at least discoverable. */
        bookScroll: { durationS: 16 },
        /** PageRail (BookUI): the bottom strip with one bar per book spread,
         *  the sliding frame that tracks hover/active, and the mute/float
         *  icon buttons. Mirrors the portfolio scroll-rail's two-layer
         *  feel — discrete bars + a sliding frame on top — with the same
         *  navlink curve as the nav link hovers and the same UI curve as
         *  the portfolio scroll-rail's frame. */
        pageRail: {
            /** Bar / spine color fade as hover and active state change. */
            bar: { durationMs: 200, easing: easings.navlink },
            /** Sliding frame transform + width. UI curve to match the
             *  portfolio scroll-rail's frame (same shape, same family). */
            frame: { durationMs: 500, easing: easings.ui },
            /** Mute / float icon-button hover opacity. */
            iconButtonHover: { durationMs: 200, easing: easings.navlink },
        },
    },

    // ═════════════════════════════════════════════════════════════════
    // 5. PORTFOLIO (carousel page)
    // ═════════════════════════════════════════════════════════════════

    /** Portfolio carousel page. */
    portfolio: {
        /** WebGL carousel slide-in — plays once on page mount. (On
         *  case-study return the content `recede` handles motion instead.) */
        scene: {
            durationMs: 700,
            easing: easings.enter,
        },
        /** First-load entrance: the loader and the carousel are one WebGL
         *  scene (components/Portfolio/slidePose.ts). Two motion values
         *  drive it and nothing else is animated by hand:
         *   - `formation` 0 → 1: the slides start stacked as one card at the
         *     front of a small ring and are dealt round it one after
         *     another, each on an ease-out cubic, fading in as it lands.
         *   - `expansion` 0 → 1: the ring unrolls into the carousel arc, the
         *     slides grow to full size, the camera travels from its start
         *     pose to the layout's home pose, and the backdrop crossfades
         *     from the loader's off-white to the dark carousel page.
         *  Expansion starts when formation has finished AND the scene is
         *  ready (strip pages uploaded), whichever is later. */
        entrance: {
            formation: {
                durationMs: 2300,
                /** The formation clock itself runs linearly. */
                easing: "linear",
                /** Each card's swing round the ring: ease-out cubic (the
                 *  `sweep` curve). */
                cardEasing: easings.sweep,
                cardBezier: easingTuples.sweep,
                /** Share of the formation the longest deal (the last slide,
                 *  which travels furthest) takes; deals are proportional to
                 *  distance and staggered so the shortest lands at 1. */
                travelShare: 0.7,
                /** Share of its own deal over which a card fades in. */
                fadeShare: 0.3,
                /** Share of the formation over which the front card (slide
                 *  0, which never travels) fades in. */
                firstCardFadeShare: 0.12,
                /** Smoothstep that fade instead of a linear ramp. */
                firstCardFadeEased: false,
                /** The deck (front card plus the cards stacked behind it)
                 *  settles into its slot from `depth` group units behind and
                 *  `scale` times its size, over `share` of the formation on
                 *  the card ease. share 0 = the deck is simply there. */
                deckArrival: { share: 0, depth: 1.5, scale: 0.94 },
                /** Share of the formation before the first card leaves the
                 *  deck (the deal runs on the remainder). 0 = the deal starts
                 *  with the formation. */
                dealDelay: 0,
                /** The front card is dealt too: a full turn of the ring back to
                 *  its slot, leading the others (firstCardFadeShare unused). */
                firstCardCycle: true,
            },
            expansion: {
                durationMs: 1800,
                /** A soft ease-in: the last cards are still landing when
                 *  the unroll begins (see startAtFormation), so the head
                 *  must not sit still, but it should not snatch either. */
                easing: "cubic-bezier(0.3, 0, 0.15, 1)",
                bezier: [0.3, 0, 0.15, 1] as const,
                /** The formation value at which the unroll may begin (once
                 *  every page has loaded). Below 1 the two overlap: the ring
                 *  never parks between the last card landing and the unroll
                 *  becoming visible. 0.9 is where the last card departs; it
                 *  lands while the expansion is still in its first few percent. */
                startAtFormation: 0.9,
            },
            ring: {
                /** Ring radius at expansion 0, in carousel units (a slide is
                 *  18 × 11.25 at full size; the arc's radius is 17.6). The
                 *  card scale follows: N scaled slides close the ring. */
                radius: 5,
                /** Deck spacing of the not-yet-dealt cards behind the front
                 *  card (carousel units at full scale). */
                stackGap: 0.04,
            },
            /** Camera poses, both in the carousel group's space (so the
             *  ring looks the same on either layout's tilt). `start` is
             *  where the loader watches the ring from: outside it, above
             *  and to the side, looking at the ring's centre, rolled. `end`
             *  is the layout's home pose (LAYOUT_CAMERA / LAYOUT_DESKTOP /
             *  LAYOUT_MOBILE in portfolioConfig.ts, fitted to the slide),
             *  the pose the carousel always had. Expansion slerps between
             *  them. */
            camera: {
                /** Per layout: a phone's narrow frame needs the camera
                 *  further out to hold the whole ring.
                 *  Front-oblique: the ring reads as a band receding into
                 *  depth rather than a drum seen from the side. */
                start: {
                    desktop: {
                        position: [9, 5, 20] as const,
                        /** Offset from the ring centre the start pose looks at. */
                        targetOffset: [0, 0, 0] as const,
                        rollDeg: -4,
                    },
                    mobile: {
                        position: [16, 9, 35] as const,
                        targetOffset: [0, 0, 0] as const,
                        rollDeg: -3,
                    },
                },
                end: "home" as const,
            },
            /** Backdrop: the loader's off-white crossfades to the dark page
             *  across this window of expansion (0 → 1). */
            backdrop: {
                color: "#f5f5f0",
                fadeFrom: 0.1,
                fadeTo: 0.75,
            },
            /** Nav, slide titles and footer start entering at this
             *  expansion, while the slides are still settling: overlap,
             *  never queue. */
            revealAt: 0.55,
            /** The ring turning about its axis while it loads, the way the
             *  cards are dealt, so the loader is never still (ringSpin.ts).
             *  It starts turned back by what it turns before a timely
             *  unroll, and comes to rest with slide 0 at the front just as
             *  the unroll starts, so the unroll plays exactly as it would
             *  without a spin. */
            spin: {
                /** One turn at cruise (ms). */
                turnMs: 9000,
                /** On time, the ring eases from cruise to rest over the last
                 *  this before the unroll (ms). */
                landMs: 700,
                /** The fastest the ring may turn to come into place in time
                 *  (× cruise); past that, the unroll waits for it. */
                maxScale: 2.5,
                /** While the deal is held for a page the ring crawls at this
                 *  (× cruise), settling as it nears how far ahead of time the
                 *  landing can take back: on a long hold late in the deal it
                 *  all but stops, rather than run past its place and hold up
                 *  the unroll while it comes round. */
                holdScale: 0.25,
                /** While held, never faster than the slack left over this
                 *  (ms): it settles into it with no jolt and cannot pass it. */
                settleMs: 300,
                /** Time constant of any change of speed (ms). */
                rampMs: 400,
            },
            /** Once the counter has landed on 100 and the ring is complete,
             *  both are held this long before the unroll and the loader's
             *  fade begin: a beat on the finished ring, not a snatch. */
            handoffHoldMs: 150,
            /** Loader counter + caption + skip fading out once expansion
             *  starts. */
            loaderFadeOut: {
                durationMs: 400,
                easing: easings.ui,
            },
            /** Nav links, subtitle and footer fading in at `revealAt`. */
            chromeIn: {
                durationMs: 700,
                easing: easings.gentle,
            },
            /** Real progress shares shown by the counter (sum 1): the page
             *  chunk, the strip page bytes, the fonts, the first draw. */
            progress: {
                chunk: 0.15,
                strip: 0.7,
                fonts: 0.05,
                scene: 0.1,
                /** The counter's digits follow the real value through two
                 *  cascaded lags of this time constant (ms) each
                 *  (counterMotion.ts), no faster than
                 *  pageTransition.counter.maxUnitsPerS: each column is an
                 *  odometer wheel driven by the smoothed value, so no
                 *  digit ever snaps, and a burst of bytes reads as a
                 *  steady climb, not a jolt. The unroll waits for the
                 *  digits to land on 100 (countDoneSafetyMs below). */
                smoothingMs: 150,
                /** The unroll stops waiting for the counter this long
                 *  after the scene is ready and the ring complete, in case
                 *  it never reports: longer than the longest finish plus
                 *  the handoff hold. */
                countDoneSafetyMs: 2500,
            },
        },
        /** Slide-indicator rail (bottom-right). */
        scrollRail: {
            frameDurationMs: 500,
            frameEasing: easings.ui,
            indicatorColorDurationMs: 200,
        },
        /** Click-to-slide scroll (rail dot → carousel scrolls to that slide).
         *  Uses the UI curve (a piece of UI scrolling, not the whole screen
         *  committing to a new state). Driven through Lenis `scrollTo`,
         *  which takes a `(t) => number` evaluator rather than a CSS string,
         *  `bezier` carries the four control points so the curve can be
         *  reconstructed in JS via `cubicBezier(...)`. `easing` is the
         *  CSS-string twin, kept for reference and to make the relationship
         *  to the rest of the UI-curve animations obvious at a glance. */
        scrollToSlide: {
            durationS: 1.0,
            bezier: easingTuples.ui,
            easing: easings.ui,
        },
        /** Per-slide arrow button hover micro-interaction (AnimatedTitle).
         *  Drives all four animated layers — dark bg out, white bg in,
         *  white arrow out, black arrow in — so they share one tempo.
         *  Pushed into the button as CSS vars (`--ab-dur/ease/delay`)
         *  consumed by Tailwind arbitrary-value classes. */
        arrowButton: {
            durationMs: 500,
            easing: easings.ui,
            delayMs: 75,
        },
        /** The whole portfolio content receding (slide left + fade) while a
         *  case study is open; reverses on close. `translateX` is tuned so
         *  the carousel slide's left edge meets the screen's left edge.
         *  durationMs should match caseStudy.panel.durationMs. */
        recede: {
            opacity: 0.2,
            translateX: "-15vw",
            durationMs: 750,
            easing: easings.chrome,
            /** Extra delay before the AnimatedTitle plays its char-reverse
             *  during the recede (entering a case study). Not applied on
             *  carousel slide changes. */
            titleReverseDelayMs: 150,
        },
    },

    // ═════════════════════════════════════════════════════════════════
    // 6. CASE STUDY (slide-over pages)
    // ═════════════════════════════════════════════════════════════════

    /** Case-study slide-over pages. */
    caseStudy: {
        /** The panel sliding in over / out from the carousel (case studies
         *  and the About panel share it). */
        panel: {
            durationMs: 750,
            easing: easings.chrome,
            /** Off-screen resting position (enter from / exit to), per
             *  edge: case studies come from the right, About from the left
             *  (Panel.from in CaseStudy/registry.ts). */
            offscreen: {
                right: "translateX(100%)",
                left: "translateX(-100%)",
            },
            /** prefers-reduced-motion: a plain fade instead of the slide. */
            reduced: { durationMs: 200, easing: "ease" },
        },
        /** Safety unmount if `transitionend` never fires. Keep ≥ panel
         *  durations so the exit animation isn't cut short. */
        exitFallbackMs: 1200,
        /** Smooth-scroll lerp for the panel's own Lenis instance. Lenis
         *  only stops within 0.5px of the target, so a low lerp ends every
         *  scroll in a long sub-pixel crawl (0.1 ≈ 0.5s of 0–0.5px steps,
         *  read as lag on stop, worse on trackpads where it stacks on the
         *  OS momentum). 0.15 cuts that tail to ≈0.3s. */
        scroll: {
            lerp: 0.15,
            /** Wheel/trackpad distance per notch (Lenis default 1).
             *  Dev only: try values with `?panelWheel=0.7` in the URL. */
            wheelMultiplier: 0.85,
        },
        /** The heroes and posters the carousel warms for instant panel
         *  opens (Portfolio.tsx): under a panel opened by deep link they
         *  wait for that panel's own hero to load, at most this long
         *  (ms), so it isn't queued behind them on a slow connection. */
        panelImageWarm: { heroWaitMs: 4000 },
        /** Non-priority image fade-in (CaseStudyImage). */
        imageFadeMs: 500,
        /** CaseStudyVideo loading sequence, every step a crossfade: inline
         *  preview → poster → video (clip and glow alike). `gentle` is the
         *  site's easing for 2D content fading into place; same duration as
         *  the case-study image fade-in (imageFadeMs). */
        video: { crossfadeMs: 500, crossfadeEasing: easings.gentle },
        /** Scroll-driven parallax on CaseStudyImage. Not an eased tween,
         *  the values are spatial ratios driven directly by scroll progress.
         *  Surfaced here so the two coupled constants live alongside the
         *  rest of the case-study motion tokens — they need to stay in
         *  lockstep (bleed ≥ strength, else the image reveals empty
         *  container behind it at the extremes of the parallax range). */
        imageParallax: {
            /** Max image translation as a fraction of the container's height.
             *  Larger = more aggressive parallax. */
            strength: 0.06,
            /** How much taller the `<img>` is than its container, on each
             *  side. MUST be ≥ strength so the image never reveals empty
             *  container behind it. */
            bleed: 0.08,
        },
        /** Global Nav / Footer color transition, synced to the panel. */
        chrome: { durationMs: 750, easing: easings.chrome },
        /** Footer fades out on case-study pages, back in elsewhere. */
        footerFade: {
            hiddenOpacity: 0,
            durationMs: 750,
            easing: easings.chrome,
        },
        /** Extra delay before the hero titleReveal plays when ENTERING a
         *  case study (lets the panel slide in first). Applied on mount
         *  only — the title leaves with no added delay on close. */
        titleEnterDelayMs: 500,
    },
} as const;
